import type { DefaultError } from "@tanstack/vue-query";
import { isAxiosError } from "axios";
import camelCase from "lodash-es/camelCase";

const isNotFoundError = (error: DefaultError) => {
  const notFoundError =
    error.cause &&
    typeof error.cause === "object" &&
    "statusCode" in error.cause &&
    error.cause.statusCode === 404;

  return Boolean(notFoundError);
};

const notFoundStopRetryQueryOptions = {
  retry: (failureCount: number, error: DefaultError) => {
    // Don't retry 404s
    if (isNotFoundError(error)) {
      return false;
    }

    return failureCount < 2;
  },
};

const notFoundQueryOptions = {
  ...notFoundStopRetryQueryOptions,
  throwOnError: isNotFoundError,
};

/**
 * Get error messages from error object from the backend, mapped on property name. Will return properties in camelCase
 * @param error
 * @returns {Record<string, string[]>>}
 */
export function getErrorsPerProperty(error: unknown): Record<string, string[]> {
  const failureMessages: Record<string, string[]> = {};

  if (!error || !isAxiosError(error) || !error.response) {
    return failureMessages;
  }

  const errorResponseData = error.response.data as
    | { failures: Failure[] }
    | undefined;

  if (errorResponseData?.failures) {
    const failures = errorResponseData.failures;

    failures.forEach((f) => {
      if (!f.propertyName) return;

      const propertyName = camelCase(f.propertyName);
      failureMessages[propertyName] = [
        ...(failureMessages[propertyName] ?? []),
        f.errorMessage,
      ];
    });
  }

  return failureMessages;
}

export { notFoundStopRetryQueryOptions, notFoundQueryOptions, isNotFoundError };
